import { ChangeDetectionStrategy, Component, ElementRef, Input, OnChanges, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { App } from '@df/core/app';
import { DfDomModule } from '@df/dom/df-dom.module';
import { ElementHeightService } from '@df/dom/element-height/element-height.service';
import { TimeoutService } from '@df/ng/timeout.service';
import { BlockStickyService } from '@df/ui/block-sticky.service';
import { Subscription } from 'rxjs';
import { StickyFreeService } from './sticky-free.service';

let uid = 0;

@Component({
    selector: 'sticky-free',
    templateUrl: './sticky-free.html',
    styleUrls: ['./sticky-free.scss'],
    standalone: true,
    imports: [DfDomModule],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class StickyFreeComponent implements OnInit, OnDestroy, OnChanges {
    private readonly _uid = `stickyfree${uid++}`;

    @Input()
    order?: number;

    @Input()
    stickyOff?: boolean;

    @Input()
    stickyId?: string;

    get uid() {
        return this.stickyId || this._uid;
    }

    private isSticky = false;

    private initiated = false;

    private unregister: (() => void) | undefined;
    private subscription: Subscription | undefined;
    private initialSubscription: Subscription | undefined;
    private subscriptions = new Subscription();

    constructor(
        private renderer2: Renderer2,
        private elementRef: ElementRef,
        private stickyFreeService: StickyFreeService,
        private elementHeightService: ElementHeightService,
        private app: App,
        private timeoutService: TimeoutService,
        private blockStickyService: BlockStickyService
    ) {}

    ngOnInit() {
        if (this.app.isPlatformBrowser) {
            this.applySticky();
            this.initialSubscription = this.elementHeightService.getState(this.uid).subscribe(value => {
                if (value !== undefined) {
                    this.renderer2.setStyle(this.elementRef.nativeElement, 'height', this.stickyFreeService.toVar(this.uid));
                    this.renderer2.addClass(this.elementRef.nativeElement, 'is-ready');
                    // const isSafari = true || /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
                    // // its only needed for safari below 15.4, we can drop it in about 1y
                    // if (isSafari) {
                    this.elementRef.nativeElement.style.setProperty(
                        '--in-app-shift',
                        `${this.elementRef.nativeElement.getBoundingClientRect().top + this.app.client.getRootElement().scrollTop}px`
                    );
                    // }
                    this.timeoutService.setTimeout(() => {
                        this.initialSubscription?.unsubscribe();
                        this.initialSubscription = undefined;
                    });
                }
            });
            this.subscriptions.add(this.blockStickyService.state$.subscribe(() => this.initiated && this.applySticky()));
            this.initiated = true;
        }
    }

    ngOnChanges(changes) {
        if (this.initiated) {
            if (changes.stickyOff) {
                this.applySticky();
            }
        }
    }

    ngOnDestroy() {
        this.unsetSticky();
        this.initialSubscription?.unsubscribe();
        this.subscriptions.unsubscribe();
    }

    private applySticky() {
        this.stickyOff || this.blockStickyService.state$.value ? this.unsetSticky() : this.setSticky();
    }

    private setSticky() {
        if (this.isSticky) {
            return;
        }
        this.isSticky = true;
        this.renderer2.removeClass(this.elementRef.nativeElement, 'isOff');
        if (this.order !== undefined) {
            this.subscription = this.stickyFreeService.entriesChange.subscribe(() => {
                if (this.order !== undefined) {
                    this.elementRef.nativeElement.style.setProperty('--sticky-offset', this.stickyFreeService.getOffsets(this.order));
                }
            });
            this.unregister = this.stickyFreeService.add({
                uid: this.uid,
                order: this.order
            });
        }
    }

    private unsetSticky() {
        if (!this.isSticky) {
            return;
        }
        this.isSticky = false;
        this.renderer2.addClass(this.elementRef.nativeElement, 'isOff');
        if (this.unregister) {
            this.unregister();
            this.unregister = undefined;
        }
        if (this.subscription) {
            this.subscription.unsubscribe();
            this.subscription = undefined;
        }
    }
}
